/**
 * V55.22: 좌석배치도 오브젝트 노드 컴포넌트
 *
 * React Flow 커스텀 노드로 각 오브젝트 타입을 렌더링합니다.
 *
 * V55.22 변경사항:
 * - floor 타입 추가 (최하위 레이어, 텍스트 정렬 지원)
 * - textBox 타입 추가 (텍스트 박스, 색상/크기 조절)
 *
 * V54.10 변경사항:
 * - 좌석 타입: 아이콘 완전 제거
 * - 좌석 타입: 직원 배정 시 이름+직함 2줄 중앙 정렬
 *
 * @module pages/organization/components/seatMap/SeatMapObjectNode
 */

import React, { memo } from 'react';
import {
  User,
  Printer,
  Users,
  Coffee,
  Package,
  Square,
  Minus,
  LayoutGrid,
  AlertTriangle,
  Layers,  // V55.22: Floor 아이콘
  Type,    // V55.22: TextBox 아이콘
} from 'lucide-react';

import {
  SeatMapObject,
  SeatMapObjectType,
  SEAT_MAP_OBJECT_CONFIGS,
  formatSeatCode,
  TextAlignX,
  TextAlignY,
} from '../../../../shared/types/seatMap';
import { useSeatMapContext } from './SeatMapContext';

// ============================================
// 아이콘 매핑
// ============================================

const iconMap: Record<SeatMapObjectType, React.ComponentType<{ size?: number; color?: string }>> = {
  floor: Layers,      // V55.22
  zone: LayoutGrid,
  seat: User,
  printer: Printer,
  meeting_room: Users,
  kitchen: Coffee,
  storage: Package,
  desk: Square,
  partition: Minus,
  textBox: Type,      // V55.22
};

// ============================================
// 색상 매핑
// ============================================

const colorMap: Record<SeatMapObjectType, { bg: string; border: string; text: string }> = {
  // V55.22: floor - 연한 회색 배경 (최하위 레이어이므로 반투명 허용)
  floor: {
    bg: 'rgba(226, 232, 240, 0.8)',
    border: '#cbd5e1',
    text: '#475569',
  },
  // V55.22: zone - 불투명 배경 (floor 비침 방지)
  zone: {
    bg: '#f5f7fa',
    border: '#94a3b8',
    text: '#475569',
  },
  seat: {
    bg: '#dbeafe',
    border: '#3b82f6',
    text: '#1e40af',
  },
  printer: {
    bg: '#f3e8ff',
    border: '#9333ea',
    text: '#6b21a8',
  },
  meeting_room: {
    bg: '#dcfce7',
    border: '#22c55e',
    text: '#166534',
  },
  kitchen: {
    bg: '#fef3c7',
    border: '#f59e0b',
    text: '#92400e',
  },
  storage: {
    bg: '#e0e7ff',
    border: '#6366f1',
    text: '#3730a3',
  },
  desk: {
    bg: '#f1f5f9',
    border: '#64748b',
    text: '#334155',
  },
  partition: {
    bg: '#e5e7eb',
    border: '#6b7280',
    text: '#374151',
  },
  // V55.22: textBox - 불투명 흰색 배경 (floor 비침 방지)
  textBox: {
    bg: '#ffffff',
    border: '#94a3b8',
    text: '#111827',
  },
};

// ============================================
// V52.10: 노드 Data 타입 (UI 상태 제거됨)
// ============================================

/**
 * V52.10: node.data에는 구조 정보만 포함
 * - isSelected, isEditMode, isDuplicate 제거됨
 * - 선택: React Flow의 selected prop
 * - 편집모드/중복: SeatMapContext
 */
export interface SeatMapObjectNodeData extends SeatMapObject {
  /** V52.2: 좌석의 zone 코드 (부모에서 zoneId로 조회하여 전달) */
  resolvedZoneCode?: string;
}

// ============================================
// 노드 컴포넌트
// ============================================

/**
 * V52.10: React Flow NodeProps에서 selected, id 등을 받음
 * 타입 캐스팅으로 NodeTypes 호환성 확보
 */
function SeatMapObjectNodeComponent(props: { data: SeatMapObjectNodeData; selected?: boolean }) {
  const { data, selected = false } = props;
  // V52.10: Context에서 UI 상태 읽기
  const { isEditMode, duplicateSeatCodes } = useSeatMapContext();

  const {
    type,
    width,
    height,
    label,
    employeeId,
    employeeName,
    employeePosition,
    zoneCode,
    seatNo,
    resolvedZoneCode,
    // V55.22: textBox/floor 관련 필드
    text,
    fontSize = 16,
    textColor = '#111827',
    alignX = 'center',
    alignY = 'middle',
  } = data;

  const config = SEAT_MAP_OBJECT_CONFIGS[type];
  const colors = colorMap[type];
  const IconComponent = iconMap[type];

  // 좌석에 직원이 배정된 경우
  const hasEmployee = type === 'seat' && employeeId;

  // V52.10: 좌석 코드 계산 및 중복 여부 확인
  const seatCode = type === 'seat' ? formatSeatCode(resolvedZoneCode, seatNo) : '';
  const isDuplicate = type === 'seat' && seatCode && duplicateSeatCodes.has(seatCode);

  // V52.2: zone은 다른 borderRadius
  const getBorderRadius = () => {
    if (type === 'partition') return '2px';
    if (type === 'zone') return '4px';
    return '8px';
  };

  /**
   * V53.1: 박스 그림자 결정 (선택/중복 강조를 box-shadow로 처리)
   *
   * RCA: ResizeObserver loop 에러의 원인은 선택 시 border 색상이 변경되면서
   * React Flow의 ResizeObserver와 렌더링 타이밍 충돌이 발생했기 때문.
   *
   * 해결: border는 항상 정적 색상(colors.border)으로 유지하고,
   * 선택/중복 강조는 box-shadow의 inset-like 효과(0 0 0 2px ...)로 처리.
   * 이렇게 하면 layout box 크기가 절대 변하지 않음.
   */
  const getBoxShadow = () => {
    // 선택 시: 파란색 하이라이트 링 + 글로우
    if (selected) {
      return '0 0 0 2px #3b82f6, 0 0 0 5px rgba(59, 130, 246, 0.3)';
    }
    // 중복 시: 빨간색 하이라이트 링 + 글로우
    if (isDuplicate) {
      return '0 0 0 2px #ef4444, 0 0 0 4px rgba(239, 68, 68, 0.3)';
    }
    // zone은 그림자 없음
    if (type === 'zone') return 'none';
    // 기본: 미세한 그림자
    return '0 1px 3px rgba(0,0,0,0.1)';
  };

  return (
    <div
      style={{
        position: 'relative',
        width,
        height,
        background: hasEmployee
          ? '#e0f2fe'
          : isDuplicate
            ? '#fef2f2'
            : colors.bg,
        // V53.1: 정적 테두리 색상 (선택/중복 강조는 box-shadow로 처리)
        border: `2px solid ${colors.border}`,
        borderRadius: getBorderRadius(),
        borderStyle: type === 'zone' ? 'dashed' : 'solid',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: getBoxShadow(),
        // V53.1: box-shadow만 트랜지션 (layout에 영향 없는 속성만)
        transition: 'box-shadow 0.15s ease-out',
        cursor: isEditMode ? 'grab' : 'default',
        overflow: 'hidden',
        padding: type === 'zone' ? '0' : '4px',
      }}
    >
      {/* V54.10: 좌석 타입 - 아이콘 제거, 텍스트 중앙 정렬 */}
      {type === 'seat' && (
        <>
          {hasEmployee ? (
            // V54.10: 직원 배정 시 - 이름+직함 2줄, 가로/세로 중앙 정렬
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                height: '100%',
                textAlign: 'center',
                lineHeight: 1.3,
              }}
            >
              <div
                style={{
                  fontSize: '12px',
                  fontWeight: '600',
                  color: '#0369a1',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: width - 8,
                }}
              >
                {employeeName}
              </div>
              {employeePosition && (
                <div
                  style={{
                    fontSize: '10px',
                    color: '#64748b',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: width - 8,
                    marginTop: '2px',
                  }}
                >
                  {employeePosition}
                </div>
              )}
            </div>
          ) : (
            // 빈 좌석 - 좌석코드 중앙 정렬
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                height: '100%',
                fontSize: '11px',
                fontWeight: seatCode ? '600' : '400',
                color: isDuplicate ? '#dc2626' : colors.text,
              }}
            >
              {seatCode || '빈 좌석'}
            </div>
          )}
          {/* V52.3: 중복 경고 아이콘 (우상단) */}
          {isDuplicate && (
            <div
              style={{
                position: 'absolute',
                top: '-4px',
                right: '-4px',
                width: '16px',
                height: '16px',
                borderRadius: '50%',
                background: '#ef4444',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              title="중복 좌석 코드"
            >
              <AlertTriangle size={10} color="white" />
            </div>
          )}
        </>
      )}

      {/* V54.6: Zone 타입 - 상단 중앙에 라벨 텍스트만 표시 (박스 제거) */}
      {type === 'zone' && (
        <div
          style={{
            position: 'absolute',
            top: '10px',
            left: 0,
            right: 0,
            display: 'flex',
            justifyContent: 'center',
            pointerEvents: 'none',
          }}
        >
          <div
            style={{
              fontSize: '18px',
              fontWeight: '700',
              color: colors.text,
              textShadow: '0 1px 2px rgba(255, 255, 255, 0.8)',
            }}
          >
            {label || (zoneCode ? `${zoneCode}구역` : config.label)}
          </div>
        </div>
      )}

      {/* V54.6: 회의실 타입 - 아이콘 제거, 이름만 중앙 표시 */}
      {type === 'meeting_room' && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: '100%',
            padding: '8px',
          }}
        >
          <div
            style={{
              fontSize: Math.min(Math.max(14, Math.min(width, height) * 0.12), 24),
              fontWeight: '600',
              color: colors.text,
              textAlign: 'center',
              wordBreak: 'keep-all',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: '100%',
            }}
          >
            {label || config.label}
          </div>
        </div>
      )}

      {/* V55.22: Floor 타입 - 텍스트 정렬 지원 */}
      {type === 'floor' && (
        <div
          style={{
            display: 'flex',
            width: '100%',
            height: '100%',
            padding: '12px',
            alignItems: alignY === 'top' ? 'flex-start' : alignY === 'bottom' ? 'flex-end' : 'center',
            justifyContent: alignX === 'left' ? 'flex-start' : alignX === 'right' ? 'flex-end' : 'center',
          }}
        >
          <div
            style={{
              fontSize: fontSize,
              fontWeight: '600',
              color: textColor,
              textAlign: alignX,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              overflow: 'hidden',
              maxWidth: '100%',
              lineHeight: 1.4,
            }}
          >
            {text || label || '바닥'}
          </div>
        </div>
      )}

      {/* V55.22: TextBox 타입 - 텍스트 박스 */}
      {type === 'textBox' && (
        <div
          style={{
            display: 'flex',
            width: '100%',
            height: '100%',
            padding: '8px',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <div
            style={{
              fontSize: fontSize,
              fontWeight: '500',
              color: textColor,
              textAlign: 'center',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              overflow: 'hidden',
              maxWidth: '100%',
              lineHeight: 1.4,
            }}
          >
            {text || label || '텍스트'}
          </div>
        </div>
      )}

      {/* 기타 타입: 아이콘 + 라벨 (회의실, floor, textBox 제외) */}
      {type !== 'seat' && type !== 'partition' && type !== 'zone' && type !== 'meeting_room' && type !== 'floor' && type !== 'textBox' && (
        <>
          <IconComponent size={Math.min(width, height) * 0.3} color={colors.text} />
          <div
            style={{
              marginTop: '4px',
              fontSize: '11px',
              fontWeight: '500',
              color: colors.text,
              textAlign: 'center',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: width - 8,
            }}
          >
            {label || config.label}
          </div>
        </>
      )}

      {/* 파티션: 심플 표시 */}
      {type === 'partition' && (
        <div
          style={{
            width: '100%',
            height: '100%',
            background: `repeating-linear-gradient(
              90deg,
              ${colors.border},
              ${colors.border} 4px,
              transparent 4px,
              transparent 8px
            )`,
          }}
        />
      )}
    </div>
  );
}

export const SeatMapObjectNode = memo(SeatMapObjectNodeComponent);
